import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import type { PromptCollection } from "@/lib/types";

export function useCollections(userId?: string) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;

  return useQuery({
    queryKey: ["collections", targetUserId],
    queryFn: async (): Promise<PromptCollection[]> => {
      if (!targetUserId) return [];

      const { data, error } = await supabase
        .from("prompt_collections")
        .select(`
          *,
          collection_prompts(count)
        `)
        .eq("user_id", targetUserId)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((c: any) => ({
        id: c.id,
        userId: c.user_id,
        name: c.name,
        description: c.description,
        isPublic: c.is_public,
        emoji: c.emoji || "📁",
        createdAt: c.created_at,
        updatedAt: c.updated_at,
        promptCount: c.collection_prompts?.[0]?.count || 0,
      }));
    },
    enabled: !!targetUserId,
  });
}

export function useCreateCollection() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (data: { name: string; description?: string; emoji?: string; isPublic?: boolean }) => {
      if (!user) throw new Error("Must be logged in");

      const { data: collection, error } = await supabase
        .from("prompt_collections")
        .insert({
          user_id: user.id,
          name: data.name,
          description: data.description || null,
          emoji: data.emoji || "📁",
          is_public: data.isPublic || false,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error("A collection with this name already exists");
        }
        throw error;
      }

      return collection;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      toast.success("Collection created!");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to create collection");
    },
  });
}

export function useUpdateCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; name?: string; description?: string; emoji?: string; isPublic?: boolean }) => {
      const updates: Record<string, any> = {};
      if (data.name !== undefined) updates.name = data.name;
      if (data.description !== undefined) updates.description = data.description;
      if (data.emoji !== undefined) updates.emoji = data.emoji;
      if (data.isPublic !== undefined) updates.is_public = data.isPublic;

      const { error } = await supabase
        .from("prompt_collections")
        .update(updates)
        .eq("id", data.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      toast.success("Collection updated!");
    },
    onError: () => {
      toast.error("Failed to update collection");
    },
  });
}

export function useDeleteCollection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (collectionId: string) => {
      const { error } = await supabase
        .from("prompt_collections")
        .delete()
        .eq("id", collectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      toast.success("Collection deleted");
    },
    onError: () => {
      toast.error("Failed to delete collection");
    },
  });
}
